/**
 * Team & Assistants — enterprise-grade team management.
 * Barrister owns the chamber. Assistants are delegated operators.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Users, UserPlus, ShieldCheck, DotsThree, PencilSimple,
  Trash, Prohibit, ArrowCounterClockwise, Eye,
  CheckCircle, Clock, CircleNotch, X,
  EnvelopeSimple, Phone, CalendarBlank, Lightning,
  ClockCountdown, Copy,
} from '@phosphor-icons/react';
import {
  fetchTeam, fetchInvitations, fetchTeamStats, fetchTeamMetadata,
  inviteAssistant, cancelInvitation, updateMemberStatus,
  removeMember, fetchMemberActivity,
  fetchMemberMatters, transferMatters,
  fetchAvailableClerks, connectClerk,
  lookupEmail, type TeamMember, type TeamInvitation, type EmailLookupResult, type MatterSummary,
  type AvailableClerk,
} from '@/lib/teamApi';
import { ClerkAccessDrawer } from '@/components/team/ClerkAccessDrawer';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  isValidName, isValidEmail, isValidPhone, sanitizeName,
  NAME_ERROR, EMAIL_ERROR, PHONE_ERROR,
} from '@/lib/validators';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ─── Helpers ──────────────────────────────────────────────────

function timeAgo(iso: string) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60)    return 'just now';
  if (d < 3600)  return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  active:    { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500', label: 'Active' },
  invited:   { bg: 'bg-amber-50 dark:bg-amber-500/10',   text: 'text-amber-700 dark:text-amber-300',   dot: 'bg-amber-400 animate-pulse', label: 'Pending' },
  suspended: { bg: 'bg-red-50 dark:bg-red-500/10',     text: 'text-red-700 dark:text-red-300',     dot: 'bg-red-500',    label: 'Suspended' },
  inactive:  { bg: 'bg-slate-50 dark:bg-slate-800/40',   text: 'text-slate-600 dark:text-slate-400',   dot: 'bg-slate-400',  label: 'Deactivated' },
};

// ─── Stat card ────────────────────────────────────────────────

function StatCard({ label, value, icon, accent }: {
  label: string; value: number | string; icon: React.ReactNode;
  accent: 'blue' | 'green' | 'amber' | 'red';
}) {
  const c = { blue: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400', green: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
               amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400', red: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' }[accent];
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5">
      <div className="flex items-start justify-between">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${c}`}>{icon}</div>
      </div>
      <div className="mt-3 text-[24px] font-bold tabular tracking-tight text-foreground leading-none">{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

// ─── Member avatar ────────────────────────────────────────────

function MemberAvatar({ member, size = 'md' }: { member: TeamMember; size?: 'sm' | 'md' }) {
  const sz = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  if (member.avatarUrl) {
    return <img src={member.avatarUrl} alt={member.firstName} className={`${sz} rounded-full object-cover ring-2 ring-white`} />;
  }
  return (
    <div className={`${sz} flex items-center justify-center rounded-full bg-gradient-to-br from-brand-500/20 to-violet-500/20 font-bold text-brand-700 dark:text-brand-300 ring-2 ring-white`}>
      {getInitials(member.firstName, member.lastName)}
    </div>
  );
}

// ─── Permission toggle ────────────────────────────────────────

function PermToggle({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border/50 px-3 py-2 transition hover:bg-muted/20">
      <span className="text-xs text-foreground">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 rounded-full transition-colors ${checked ? 'bg-brand-600' : 'bg-muted'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${checked ? 'left-4' : 'left-0.5'}`} />
      </button>
    </label>
  );
}

// ─── Invite modal ─────────────────────────────────────────────

function InviteModal({ metadata, onClose, onDone }: {
  metadata: { permissionGroups: Record<string, Record<string, string>>; rolePresets: Record<string, string[]>; roleLabels: Record<string, string> };
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', roleLabel: 'clerk', note: '' });
  const [permissions, setPermissions] = useState<string[]>(metadata.rolePresets['clerk'] ?? []);
  const [saving, setSaving] = useState(false);
  // Which fields the user has left. With the Send button disabled until the form is valid,
  // a submit attempt can never happen — so 'required' messages have to surface on blur,
  // or an empty form would show a grey button and no reason for it.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (k: string) => setTouched(t => ({ ...t, [k]: true }));

  // ── Email auto-lookup ──────────────────────────────────────────
  const [lookupResult, setLookupResult]   = useState<EmailLookupResult | null>(null);
  const [lookupState, setLookupState]     = useState<'idle' | 'searching' | 'found' | 'not_found' | 'already_member'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoFilledRef = useRef(false);

  const handleEmailChange = (email: string) => {
    setForm(f => ({ ...f, email }));
    setLookupResult(null);
    setLookupState('idle');
    autoFilledRef.current = false;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!email.includes('@') || email.length < 5) return;

    setLookupState('searching');
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await lookupEmail(email);
        if (result) {
          setLookupResult(result);
          if (result.isSameChambers) {
            setLookupState('already_member');
          } else {
            setLookupState('found');
            // Auto-fill only if the user hasn't typed anything manually yet
            if (!autoFilledRef.current) {
              autoFilledRef.current = true;
              setForm(f => ({
                ...f,
                firstName: f.firstName || result.firstName,
                lastName:  f.lastName  || result.lastName,
                phone:     f.phone     || result.phone || '',
              }));
            }
          }
        } else {
          setLookupState('not_found');
        }
      } catch {
        setLookupState('not_found');
      }
    }, 400);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const togglePerm = (key: string) => setPermissions(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key]);
  const applyPreset = (role: string) => { setForm(f => ({ ...f, roleLabel: role })); setPermissions(metadata.rolePresets[role] ?? []); };

  // Field-level validation, matching NewContactPage: required errors surface only after a
  // submit attempt, format errors (digits in a name, a bare number as an email) surface live.
  // An invitation carrying an unreachable email is created, stored and silently never
  // delivered — so a bad address must be refused before it reaches the server.
  const emailError = form.email.trim()
    ? (isValidEmail(form.email) ? '' : EMAIL_ERROR)
    : (touched.email ? 'Email is required.' : '');
  const firstNameError = form.firstName.trim()
    ? (isValidName(form.firstName) ? '' : NAME_ERROR)
    : (touched.firstName ? 'First name is required.' : '');
  const lastNameError = form.lastName.trim()
    ? (isValidName(form.lastName) ? '' : NAME_ERROR)
    : (touched.lastName ? 'Last name is required.' : '');
  // Phone is optional — only its format is checked, and only once something is typed.
  const phoneError = form.phone.trim() && !isValidPhone(form.phone) ? PHONE_ERROR : '';

  const hasError =
    !form.email.trim() || !isValidEmail(form.email) ||
    !form.firstName.trim() || !isValidName(form.firstName) ||
    !form.lastName.trim() || !isValidName(form.lastName) ||
    !!phoneError;

  const handleSubmit = async () => {
    if (hasError) { toast.error('Please fix the highlighted fields.'); return; }
    if (lookupState === 'already_member') { toast.error('This person is already a member of your practice'); return; }
    setSaving(true);
    try {
      await inviteAssistant({ ...form, permissions });
      toast.success(`Invitation email sent to ${form.email}`);
      toast(`Need to share it another way? Copy the invite link from the pending list.`, { duration: 6000 });
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Failed to send invitation');
    } finally { setSaving(false); }
  };

  const inputCls = (extra = '') =>
    `w-full rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm transition-all
     focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${extra}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40 backdrop-blur-sm">
      <ScrollLock />
      <div className="flex h-full w-full max-w-xl flex-col bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-foreground">Invite Assistant</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">Add a clerk, associate, or support staff to your practice</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Email first (drives auto-fill) ─────────────── */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                value={form.email}
                onChange={e => handleEmailChange(e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="clerk@chambers.co.uk"
                className={inputCls(
                  emailError ? 'border-red-400 ring-2 ring-red-500/15' :
                  lookupState === 'found' ? 'border-emerald-400 ring-2 ring-emerald-500/15' :
                  lookupState === 'already_member' ? 'border-red-400 ring-2 ring-red-500/15' : ''
                )}
              />
              {/* Right indicator */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {lookupState === 'searching' && <CircleNotch size={14} className="animate-spin text-muted-foreground/40" />}
                {lookupState === 'found' && <CheckCircle size={14} weight="fill" className="text-emerald-500" />}
                {lookupState === 'already_member' && <X size={14} className="text-red-500" weight="bold" />}
              </div>
            </div>

            {emailError && <p className="text-xs text-red-600 dark:text-red-400">{emailError}</p>}

            {/* Lookup status banner */}
            {lookupState === 'found' && lookupResult && (
              <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200/80 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3.5 py-2.5">
                {lookupResult.avatarUrl
                  ? <img src={lookupResult.avatarUrl} className="h-7 w-7 rounded-full object-cover ring-1 ring-white" alt="" />
                  : <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                      {getInitials(lookupResult.firstName, lookupResult.lastName)}
                    </div>
                }
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                    {lookupResult.firstName} {lookupResult.lastName}
                    <span className="ml-1.5 text-[10px] font-normal text-emerald-600 dark:text-emerald-400">— details auto-filled</span>
                  </div>
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-300/70">{lookupResult.chambersName}</div>
                </div>
              </div>
            )}
            {lookupState === 'already_member' && (
              <p className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                <X size={11} weight="bold" />This person is already a member of your practice
              </p>
            )}
            {lookupState === 'not_found' && form.email.includes('@') && (
              <p className="text-[10px] text-muted-foreground/70">
                Not found in the system — fill in their details below to send an invitation.
              </p>
            )}
          </div>

          {/* ── Name (auto-filled if found) ──────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                First Name <span className="text-red-500">*</span>
                {lookupState === 'found' && <span className="rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-bold text-emerald-600 dark:text-emerald-400">AUTO</span>}
              </label>
              <input
                value={form.firstName}
                onChange={e => { autoFilledRef.current = true; setForm(f => ({ ...f, firstName: sanitizeName(e.target.value) })); }}
                onBlur={() => markTouched('firstName')}
                maxLength={100}
                placeholder="First name"
                className={inputCls(firstNameError ? 'border-red-400 ring-2 ring-red-500/15' : lookupState === 'found' && form.firstName ? 'bg-emerald-50/30 dark:bg-emerald-500/10' : '')}
              />
              {firstNameError && <p className="text-xs text-red-600 dark:text-red-400">{firstNameError}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Last Name <span className="text-red-500">*</span>
                {lookupState === 'found' && <span className="rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-bold text-emerald-600 dark:text-emerald-400">AUTO</span>}
              </label>
              <input
                value={form.lastName}
                onChange={e => { autoFilledRef.current = true; setForm(f => ({ ...f, lastName: sanitizeName(e.target.value) })); }}
                onBlur={() => markTouched('lastName')}
                maxLength={100}
                placeholder="Last name"
                className={inputCls(lastNameError ? 'border-red-400 ring-2 ring-red-500/15' : lookupState === 'found' && form.lastName ? 'bg-emerald-50/30 dark:bg-emerald-500/10' : '')}
              />
              {lastNameError && <p className="text-xs text-red-600 dark:text-red-400">{lastNameError}</p>}
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Phone
              {lookupState === 'found' && lookupResult?.phone && <span className="rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-bold text-emerald-600 dark:text-emerald-400">AUTO</span>}
            </label>
            {/* The shared PhoneInput, as every other phone field in the product uses. The
                country dial code is what gives the length rule its meaning — isValidPhone
                measures against the country the code names (UK: 10 digits after +44), so a
                bare text box could not validate length at all. */}
            <PhoneInput
              value={form.phone}
              onChange={(v) => { autoFilledRef.current = true; setForm(f => ({ ...f, phone: v })); }}
              error={phoneError || undefined}
              disabled={saving}
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</label>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(metadata.roleLabels).map(([key, label]) => (
                <button key={key} type="button" onClick={() => applyPreset(key)}
                  className={`rounded-xl border px-4 py-2 text-xs font-semibold transition-all ${
                    form.roleLabel === key
                      ? 'border-brand-400 bg-brand-50 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'border-border/60 bg-card text-muted-foreground hover:border-brand-200'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Permissions</label>
              <span className="text-[10px] text-muted-foreground">{permissions.length} enabled</span>
            </div>
            {Object.entries(metadata.permissionGroups).map(([group, perms]) => (
              <div key={group}>
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">{group}</div>
                <div className="space-y-1">
                  {Object.entries(perms).map(([key, label]) => (
                    <PermToggle key={key} label={label} checked={permissions.includes(key)} onChange={() => togglePerm(key)} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Internal Note</label>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Optional — e.g. covering maternity leave, part-time clerk..."
              rows={2} className="w-full resize-none rounded-xl border border-border/70 bg-background px-3.5 py-2.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20" />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/60 px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-[10px] text-muted-foreground">
            {lookupState === 'found'
              ? 'Existing system user — an invite link will still be generated to confirm access.'
              : 'An invite link will be generated. Share it to complete setup.'}
          </p>
          <div className="flex shrink-0 gap-2">
            <button onClick={onClose} className="rounded-xl border border-border px-4 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={saving || hasError || lookupState === 'already_member'}
              className="flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted">
              {saving ? <><CircleNotch size={13} className="animate-spin" />Sending…</> : <><UserPlus size={13} weight="bold" />Send Invitation</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Connect-existing-clerk modal ─────────────────────────────
// Instantly connect a clerk already registered in the system (no invite link).

function ConnectExistingModal({ metadata, onClose, onDone }: {
  metadata: { permissionGroups: Record<string, Record<string, string>>; rolePresets: Record<string, string[]>; roleLabels: Record<string, string> };
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: available = [], isLoading } = useQuery({
    queryKey: ['team-available'], queryFn: fetchAvailableClerks, staleTime: 0,
  });
  const [selectedId, setSelectedId] = useState('');
  const [role, setRole] = useState<'primary' | 'assistant'>('assistant');
  // Symmetry with Invite: the barrister chooses what the clerk can do at connect time, so the
  // clerk gets real access immediately. Default to the sensible `clerk` preset.
  const [preset, setPreset] = useState('clerk');
  const [permissions, setPermissions] = useState<string[]>(metadata.rolePresets['clerk'] ?? []);
  const [saving, setSaving] = useState(false);
  const selected = available.find((c: AvailableClerk) => c.id === selectedId);

  const togglePerm = (key: string) => setPermissions(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key]);
  const applyPreset = (r: string) => { setPreset(r); setPermissions(metadata.rolePresets[r] ?? []); };

  const handleConnect = async () => {
    if (!selectedId) { toast.error('Select a clerk first'); return; }
    setSaving(true);
    try {
      await connectClerk(selectedId, role, permissions);
      toast.success(`${selected?.firstName} ${selected?.lastName} connected to your practice`);
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Failed to connect clerk');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col rounded-2xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b border-border/60 px-6 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-foreground">Connect a clerk</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">Instantly link a clerk already registered in the system</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8"><CircleNotch size={18} className="animate-spin text-muted-foreground/40" /></div>
          )}
          {!isLoading && available.length === 0 && (
            <div className="rounded-xl border border-border/50 bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
              No unconnected clerks found. Use <span className="font-semibold text-foreground">Invite assistant</span> to add someone by email.
            </div>
          )}
          {!isLoading && available.length > 0 && (
            <>
              <div className="space-y-2">
                {available.map((c: AvailableClerk) => (
                  <button key={c.id} type="button" onClick={() => setSelectedId(c.id)}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                      selectedId === c.id ? 'border-brand-400 bg-brand-50' : 'border-border/60 hover:border-brand-200'
                    }`}>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-brand-700 dark:text-brand-300">
                      {c.firstName.charAt(0)}{c.lastName.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">{c.firstName} {c.lastName}</div>
                      <div className="truncate text-xs text-muted-foreground">{c.email}</div>
                    </div>
                    <span className="shrink-0 rounded-full border border-border/50 bg-muted/30 px-2 py-0.5 text-[9.5px] font-medium text-muted-foreground">
                      {c.memberType === 'senior_clerk' ? 'Senior Clerk' : 'Junior Clerk'}
                    </span>
                    {selectedId === c.id && <CheckCircle size={18} weight="fill" className="shrink-0 text-brand-600 dark:text-brand-400" />}
                  </button>
                ))}
              </div>
              {selectedId && (
                <div className="grid grid-cols-2 gap-2">
                  {(['primary', 'assistant'] as const).map((r) => (
                    <button key={r} type="button" onClick={() => setRole(r)}
                      className={`flex flex-col items-start rounded-xl border p-3 text-left transition ${
                        role === r ? 'border-brand-400 bg-brand-50' : 'border-border/60 hover:border-brand-200'
                      }`}>
                      <div className="text-sm font-semibold capitalize text-foreground">{r}</div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground">
                        {r === 'primary' ? 'Lead contact for your practice' : 'Supporting clerk'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedId && (
                <>
                  {/* Access preset chips */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Access level</label>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(metadata.roleLabels).map(([key, label]) => (
                        <button key={key} type="button" onClick={() => applyPreset(key)}
                          className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${
                            preset === key ? 'border-brand-400 bg-brand-50 text-brand-700 dark:text-brand-300' : 'border-border/60 text-muted-foreground hover:border-brand-200'
                          }`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Permission toggles */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Permissions</label>
                      <span className="text-[10px] text-muted-foreground">{permissions.length} enabled</span>
                    </div>
                    {Object.entries(metadata.permissionGroups).map(([group, perms]) => (
                      <div key={group}>
                        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">{group}</div>
                        <div className="space-y-1">
                          {Object.entries(perms).map(([key, label]) => (
                            <PermToggle key={key} label={label} checked={permissions.includes(key)} onChange={() => togglePerm(key)} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-500/10 p-3 text-xs text-blue-900 dark:text-blue-200">
                    <strong>Immediate access.</strong> {selected?.firstName} is connected right away with the permissions above, and is notified. You can fine-tune access anytime from their card.
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition">Cancel</button>
          <button onClick={handleConnect} disabled={!selectedId || saving}
            className="flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50 transition">
            {saving ? <><CircleNotch size={13} className="animate-spin" />Connecting…</> : 'Connect clerk'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Legacy PermissionsDrawer removed — replaced by ClerkAccessDrawer
// (Phase 2 capability UI: Full / Limited / No Access + per-matter exceptions).

// ─── Activity modal ────────────────────────────────────────────

function ActivityModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['team-activity', member.memberId],
    queryFn: () => fetchMemberActivity(member.memberId),
    staleTime: 0,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-xl rounded-2xl bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
          <div className="flex items-center gap-3">
            <MemberAvatar member={member} size="sm" />
            <div>
              <div className="text-sm font-bold">{member.firstName} {member.lastName}</div>
              <div className="text-[10px] text-muted-foreground">Recent activity</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50"><X size={16} /></button>
        </div>
        <div className="max-h-80 overflow-y-auto px-6 py-4 space-y-2">
          {isLoading && (
            <div className="flex h-24 items-center justify-center">
              <CircleNotch size={18} className="animate-spin text-muted-foreground/30" />
            </div>
          )}
          {!isLoading && logs.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <ClockCountdown size={24} weight="duotone" className="text-muted-foreground/20" />
              <div className="text-xs text-muted-foreground">No activity recorded yet</div>
            </div>
          )}
          {logs.map((log: any) => (
            <div key={log.id} className="flex items-start gap-3 rounded-xl border border-border/40 px-3 py-2.5">
              <div className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-muted/50 flex items-center justify-center">
                <Lightning size={10} className="text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-medium text-foreground">
                  {log.action} {log.entityType}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[9.5px] text-muted-foreground">
                  <span>{new Date(log.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  {log.actorIp && <><span>·</span><span>{log.actorIp}</span></>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-border/60 px-6 py-3">
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Remove-with-transfer modal ────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  intake: 'Intake', active: 'Active', listed: 'Listed',
  settled: 'Settled', closed_won: 'Closed', closed_lost: 'Closed', archived: 'Archived',
};

function RemoveWithTransferModal({ member, otherMembers, onClose, onDone }: {
  member: TeamMember;
  otherMembers: TeamMember[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: matters = [], isLoading: mattersLoading } = useQuery<MatterSummary[]>({
    queryKey: ['member-matters', member.memberId],
    queryFn: () => fetchMemberMatters(member.memberId),
    staleTime: 0,
  });

  const [toMemberId, setToMemberId] = useState<string>('__unassigned__');
  const [acting, setActing] = useState(false);

  const handleConfirm = async () => {
    setActing(true);
    try {
      const destination = toMemberId === '__unassigned__' ? undefined : toMemberId;
      const { transferred } = await transferMatters(member.memberId, destination);
      await removeMember(member.memberId);
      const destName = otherMembers.find(m => m.memberId === destination)?.firstName;
      const transferMsg = transferred > 0
        ? destName
          ? `${transferred} matter${transferred !== 1 ? 's' : ''} transferred to ${destName}.`
          : `${transferred} matter${transferred !== 1 ? 's' : ''} left unassigned.`
        : '';
      toast.success(`${member.firstName} ${member.lastName} removed. ${transferMsg}`);
      onDone();
    } catch {
      toast.error('Failed to complete removal');
    } finally {
      setActing(false);
    }
  };

  const SHOWN = 5;
  const visibleMatters = matters.slice(0, SHOWN);
  const hiddenCount = matters.length - SHOWN;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4">
      <ScrollLock />
      <div className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-2xl bg-card shadow-2xl">

        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-100 dark:bg-red-500/15">
            <Trash size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-foreground">
              Remove {member.firstName} {member.lastName}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              This member has <span className="font-semibold text-foreground">{member.matterCount} active matter{member.matterCount !== 1 ? 's' : ''}</span>. Transfer them before removing, or leave them unassigned.
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Active matters list */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Active matters
            </div>
            {mattersLoading ? (
              <div className="flex h-16 items-center justify-center">
                <CircleNotch size={16} className="animate-spin text-muted-foreground/30" />
              </div>
            ) : matters.length === 0 ? (
              <div className="rounded-xl border border-border/40 bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
                No active matters found — safe to remove directly.
              </div>
            ) : (
              <div className="space-y-1">
                {visibleMatters.map(m => (
                  <div key={m.id} className="flex items-center justify-between rounded-xl border border-border/40 bg-muted/10 px-3.5 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-xs font-medium text-foreground">{m.caseName}</div>
                      <div className="text-[10px] text-muted-foreground">{m.caseNumber}</div>
                    </div>
                    <span className="ml-3 shrink-0 rounded-full border border-border/50 bg-muted/30 px-2 py-0.5 text-[9.5px] font-medium text-muted-foreground">
                      {STATUS_LABEL[m.status] ?? m.status}
                    </span>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <div className="px-3.5 py-1.5 text-[10px] text-muted-foreground">
                    +{hiddenCount} more matter{hiddenCount !== 1 ? 's' : ''} will also be transferred
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Transfer destination */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Transfer matters to
            </label>
            <div className="space-y-1.5">
              <label className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-3 transition ${
                toMemberId === '__unassigned__' ? 'border-brand-400 bg-brand-50/40' : 'border-border/60 hover:border-border'
              }`}>
                <input type="radio" name="transfer-dest" value="__unassigned__"
                  checked={toMemberId === '__unassigned__'} onChange={() => setToMemberId('__unassigned__')}
                  className="accent-brand-600" />
                <div>
                  <div className="text-xs font-semibold text-foreground">Leave unassigned</div>
                  <div className="text-[10px] text-muted-foreground">Matters will have no assigned clerk until reassigned manually</div>
                </div>
              </label>
              {otherMembers.filter(m => m.isActive).map(m => (
                <label key={m.memberId} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-3 transition ${
                  toMemberId === m.memberId ? 'border-brand-400 bg-brand-50/40' : 'border-border/60 hover:border-border'
                }`}>
                  <input type="radio" name="transfer-dest" value={m.memberId}
                    checked={toMemberId === m.memberId} onChange={() => setToMemberId(m.memberId)}
                    className="accent-brand-600" />
                  <MemberAvatar member={m} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground">{m.firstName} {m.lastName}</div>
                    <div className="text-[10px] text-muted-foreground">{m.roleLabel} · {m.matterCount} matter{m.matterCount !== 1 ? 's' : ''} currently</div>
                  </div>
                </label>
              ))}
              {otherMembers.filter(m => m.isActive).length === 0 && (
                <p className="text-[10px] text-muted-foreground px-1">No other active team members — matters will be left unassigned.</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border/60 px-6 py-4">
          <p className="text-[10px] text-muted-foreground">All sessions revoked immediately on removal.</p>
          <div className="flex shrink-0 gap-2">
            <button onClick={onClose} disabled={acting}
              className="rounded-xl border border-border px-4 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={acting}
              className="flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2 text-xs font-semibold text-white hover:bg-red-700 transition disabled:opacity-60">
              {acting ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} />}
              {acting ? 'Removing…' : 'Transfer & Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Member card ──────────────────────────────────────────────

function MemberCard({ member, otherMembers, onEditPermissions, onViewActivity, onRefresh }: {
  member: TeamMember;
  otherMembers: TeamMember[];
  onEditPermissions: () => void;
  onViewActivity: () => void;
  onRefresh: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState<'suspend' | 'remove' | null>(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [acting, setActing] = useState(false);
  const st = STATUS_STYLES[member.status] ?? STATUS_STYLES.inactive!;

  const handleSuspend = async () => {
    setActing(true);
    try {
      await updateMemberStatus(member.memberId, 'suspend');
      toast.success(`${member.firstName} ${member.lastName} has been suspended`);
      onRefresh();
    } catch { toast.error('Failed to suspend member'); }
    finally { setActing(false); setConfirming(null); }
  };

  const handleReactivate = async () => {
    setActing(true);
    try {
      await updateMemberStatus(member.memberId, 'reactivate');
      toast.success(`${member.firstName} ${member.lastName} reactivated`);
      onRefresh();
    } catch { toast.error('Failed to reactivate'); }
    finally { setActing(false); }
  };

  const handleRemoveRequest = () => {
    setConfirming(null);
    if (member.matterCount > 0) {
      setShowTransfer(true);
    } else {
      setConfirming('remove');
    }
  };

  const handleRemoveDirect = async () => {
    setActing(true);
    try {
      await removeMember(member.memberId);
      toast.success(`${member.firstName} ${member.lastName} removed. All sessions revoked.`);
      onRefresh();
    } catch { toast.error('Failed to remove member'); }
    finally { setActing(false); setConfirming(null); }
  };

  return (
    <>
      {/* No `overflow-hidden` here: the row's ⋯ menu is absolutely positioned inside this card,
          so clipping the card clipped the menu — on a short card that hid Suspend access and
          Remove assistant entirely, with no way to reach them. The status stripe below is inset
          instead of clipped, so the rounded corners still read cleanly. */}
      <div className={`relative rounded-2xl border bg-card transition-all duration-150 hover:shadow-sm ${
        !member.isActive ? 'border-red-200/60 dark:border-red-500/30 bg-red-50/10 dark:bg-red-500/10' : 'border-border/60'
      }`}>
        {/* Status stripe */}
        <div className={`absolute bottom-3 left-0 top-3 w-0.5 rounded-full ${member.status === 'active' ? 'bg-emerald-500' : member.status === 'invited' ? 'bg-amber-400' : 'bg-red-400'}`} />

        <div className="px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            {/* Avatar + identity */}
            <div className="flex items-start gap-3">
              <MemberAvatar member={member} />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-foreground">{member.firstName} {member.lastName}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${st.bg} ${st.text}`}>
                    <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{member.roleLabel}</div>
              </div>
            </div>

            {/* Actions menu */}
            <div className="relative shrink-0">
              <button onClick={() => setMenuOpen(m => !m)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted/50 transition">
                <DotsThree size={16} weight="bold" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-8 z-20 w-48 rounded-2xl border border-border/60 bg-card shadow-pop">
                  <button onClick={() => { onViewActivity(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-xs text-foreground hover:bg-muted/40 transition rounded-t-2xl">
                    <Eye size={13} className="text-muted-foreground" />View activity
                  </button>
                  <button onClick={() => { onEditPermissions(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-xs text-foreground hover:bg-muted/40 transition">
                    <PencilSimple size={13} className="text-muted-foreground" />Edit permissions
                  </button>
                  {member.isActive
                    ? <button onClick={() => { setConfirming('suspend'); setMenuOpen(false); }}
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:bg-amber-500/10 transition">
                        <Prohibit size={13} />Suspend access
                      </button>
                    : <button onClick={handleReactivate}
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:bg-emerald-500/10 transition">
                        <ArrowCounterClockwise size={13} />Reactivate
                      </button>
                  }
                  <div className="mx-2 my-1 h-px bg-border/50" />
                  <button onClick={() => { setMenuOpen(false); handleRemoveRequest(); }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:bg-red-500/10 transition rounded-b-2xl">
                    <Trash size={13} />Remove assistant
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Contact info */}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><EnvelopeSimple size={11} />{member.email}</span>
            {member.phone && <span className="flex items-center gap-1.5"><Phone size={11} />{member.phone}</span>}
            <span className="flex items-center gap-1.5">
              <CalendarBlank size={11} />Joined {new Date(member.assignedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            {member.lastLoginAt && (
              <span className="flex items-center gap-1.5">
                <ClockCountdown size={11} />Last active {timeAgo(member.lastLoginAt)}
              </span>
            )}
            <span className={`flex items-center gap-1.5 font-medium ${member.matterCount > 0 ? 'text-foreground' : ''}`}>
              <Lightning size={11} className={member.matterCount > 0 ? 'text-amber-500' : ''} />
              {member.matterCount} matter{member.matterCount !== 1 ? 's' : ''} assigned
            </span>
          </div>

          {/* Permissions summary */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {member.permissions.slice(0, 5).map(p => (
              <span key={p} className="rounded-full border border-border/50 bg-muted/30 px-2.5 py-0.5 text-[9.5px] font-medium text-muted-foreground">
                {p.split('.')[1]?.replace('_', ' ')}
              </span>
            ))}
            {member.permissions.length > 5 && (
              <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-[9.5px] font-semibold text-brand-600 dark:text-brand-400">
                +{member.permissions.length - 5} more
              </span>
            )}
            {member.permissions.length === 0 && (
              <span className="text-[10px] text-muted-foreground/50">No permissions granted</span>
            )}
          </div>

          {member.note && (
            <div className="mt-2.5 rounded-lg border border-border/40 bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground italic">
              {member.note}
            </div>
          )}
        </div>
      </div>

      {/* Confirm dialogs */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-2xl bg-card p-6 shadow-2xl">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${confirming === 'remove' ? 'bg-red-100 dark:bg-red-500/15' : 'bg-amber-100 dark:bg-amber-500/15'}`}>
              {confirming === 'remove' ? <Trash size={20} className="text-red-600 dark:text-red-400" /> : <Prohibit size={20} className="text-amber-600 dark:text-amber-400" />}
            </div>
            <h3 className="text-[15px] font-bold text-foreground">
              {confirming === 'remove' ? 'Remove assistant?' : 'Suspend access?'}
            </h3>
            <p className="mt-2 text-xs text-muted-foreground">
              {confirming === 'remove'
                ? `This will immediately revoke all sessions, block login, and remove ${member.firstName} from your active team. You can reactivate them later from this page.`
                : `${member.firstName}'s access will be suspended immediately. All active sessions will be logged out.`}
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setConfirming(null)} className="rounded-xl border border-border px-4 py-2 text-xs hover:bg-muted/50">Cancel</button>
              <button
                onClick={confirming === 'remove' ? handleRemoveDirect : handleSuspend}
                disabled={acting}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold text-white transition ${
                  confirming === 'remove' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
                } disabled:opacity-60`}>
                {acting ? <CircleNotch size={13} className="animate-spin" /> : null}
                {confirming === 'remove' ? 'Yes, remove' : 'Yes, suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer-and-remove modal (clerk has active matters) */}
      {showTransfer && (
        <RemoveWithTransferModal
          member={member}
          otherMembers={otherMembers}
          onClose={() => setShowTransfer(false)}
          onDone={() => { setShowTransfer(false); onRefresh(); }}
        />
      )}
    </>
  );
}

// ─── Invitation card ──────────────────────────────────────────

function InvitationCard({ inv, onCancel }: { inv: TeamInvitation; onCancel: () => void }) {
  const isExpired = new Date(inv.expiresAt) < new Date();
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try { await cancelInvitation(inv.id); onCancel(); toast.success('Invitation cancelled'); }
    catch { toast.error('Failed to cancel invitation'); }
    finally { setCancelling(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/accept-team-invite/${inv.token}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`rounded-2xl border px-5 py-4 ${isExpired ? 'border-border/40 bg-muted/10 opacity-60' : 'border-amber-200/60 dark:border-amber-500/30 bg-amber-50/20 dark:bg-amber-500/10'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-200 dark:border-amber-500/30 bg-amber-100 dark:bg-amber-500/15 text-sm font-bold text-amber-700 dark:text-amber-300">
            {getInitials(inv.firstName, inv.lastName)}
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">{inv.firstName} {inv.lastName}</div>
            <div className="text-xs text-muted-foreground">{inv.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold ${isExpired ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
            {isExpired ? 'Expired' : 'Pending'}
          </span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Role: <span className="font-medium text-foreground">{inv.roleLabel}</span></span>
        <span>{inv.permissions.length} permissions</span>
        <span className="flex items-center gap-1"><Clock size={10} />Expires {new Date(inv.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
      </div>

      {!isExpired && (
        <div className="mt-3 flex items-center gap-2">
          <button onClick={handleCopy}
            className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground hover:bg-muted/50 transition">
            {copied ? <><CheckCircle size={10} className="text-emerald-500" />Copied</> : <><Copy size={10} />Copy invite link</>}
          </button>
          <button onClick={handleCancel} disabled={cancelling}
            className="flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-500/30 px-2.5 py-1.5 text-[10px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:bg-red-500/10 transition">
            {cancelling ? <CircleNotch size={10} className="animate-spin" /> : <X size={10} />}Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function MyTeamPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<TeamMember | null>(null);
  const [viewingActivity, setViewingActivity] = useState<TeamMember | null>(null);

  const { data: metadata } = useQuery({ queryKey: ['team-metadata'], queryFn: fetchTeamMetadata, staleTime: Infinity });
  const { data: stats } = useQuery({ queryKey: ['team-stats'], queryFn: fetchTeamStats, staleTime: 0 });
  const { data: members = [], isLoading } = useQuery({ queryKey: ['team'], queryFn: fetchTeam, staleTime: 0 });
  const { data: invitations = [] } = useQuery({ queryKey: ['team-invitations'], queryFn: fetchInvitations, staleTime: 0 });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['team'] });
    qc.invalidateQueries({ queryKey: ['team-stats'] });
    qc.invalidateQueries({ queryKey: ['team-invitations'] });
  };

  const activeMembers    = members.filter(m => m.isActive);
  const suspendedMembers = members.filter(m => !m.isActive);
  const pendingInv       = invitations.filter(i => i.status === 'pending' && new Date(i.expiresAt) > new Date());

  return (
    <div className="pb-24">

      <PageHeader
        onBack={() => navigate(-1)}
        title="My Clerks"
        subtitle="The one place to add clerks & assistants, set what they can do, and see what they are handling. You remain the practice owner."
        actions={
          <>
            <Button
              variant="outline"
              size="md"
              leftIcon={<Users size={16} weight="bold" />}
              onClick={() => setShowConnect(true)}
            >
              Connect existing
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<UserPlus size={16} weight="bold" />}
              onClick={() => setShowInvite(true)}
            >
              Invite Assistant
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total Members" value={stats?.total ?? 0} icon={<Users size={14} weight="bold" />} accent="blue" />
        <StatCard label="Active"         value={stats?.active ?? 0}     icon={<CheckCircle size={14} weight="bold" />} accent="green" />
        <StatCard label="Suspended"      value={stats?.suspended ?? 0}  icon={<Prohibit size={14} weight="bold" />}    accent="red" />
        <StatCard label="Pending Invites" value={stats?.pending ?? 0}   icon={<Clock size={14} weight="bold" />}       accent="amber" />
      </div>

      {/* Security notice */}
      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-brand-200/60 bg-brand-50/30 px-4 py-3.5">
        <ShieldCheck size={15} className="mt-0.5 shrink-0 text-brand-600 dark:text-brand-400" weight="duotone" />
        <div className="text-xs text-brand-800 dark:text-brand-200">
          <span className="font-semibold">Ownership protection: </span>
          All matters, documents, and financials belong to your chamber. Assistants are delegated operators only — they can never transfer ownership or access billing.
          Removing an assistant immediately revokes all sessions and blocks login.
        </div>
      </div>

      {/* Active team */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <CircleNotch size={20} className="animate-spin text-muted-foreground/30" />
        </div>
      ) : (
        <>
          {activeMembers.length > 0 && (
            <div className="mb-6">
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Active Members</h2>
              <div className="space-y-3">
                {activeMembers.map(m => (
                  <MemberCard key={m.memberId} member={m}
                    otherMembers={activeMembers.filter(x => x.memberId !== m.memberId)}
                    onEditPermissions={() => setEditingPermissions(m)}
                    onViewActivity={() => setViewingActivity(m)}
                    onRefresh={refresh} />
                ))}
              </div>
            </div>
          )}

          {suspendedMembers.length > 0 && (
            <div className="mb-6">
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Suspended</h2>
              <div className="space-y-3">
                {suspendedMembers.map(m => (
                  <MemberCard key={m.memberId} member={m}
                    otherMembers={activeMembers}
                    onEditPermissions={() => setEditingPermissions(m)}
                    onViewActivity={() => setViewingActivity(m)}
                    onRefresh={refresh} />
                ))}
              </div>
            </div>
          )}

          {pendingInv.length > 0 && (
            <div className="mb-6">
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Pending Invitations</h2>
              <div className="space-y-3">
                {pendingInv.map(inv => <InvitationCard key={inv.id} inv={inv} onCancel={refresh} />)}
              </div>
            </div>
          )}

          {members.length === 0 && pendingInv.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/30">
                <Users size={28} weight="duotone" className="text-muted-foreground/30" />
              </div>
              <div className="text-sm font-semibold text-foreground">No team members yet</div>
              <div className="text-xs text-muted-foreground max-w-xs">
                Invite a clerk or assistant to help manage your practice. You retain full ownership of all data.
              </div>
              <button onClick={() => setShowInvite(true)}
                className="mt-2 flex items-center gap-2 rounded-2xl bg-brand-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-brand-700 transition">
                <UserPlus size={13} weight="bold" />Invite your first assistant
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showInvite && metadata && (
        <InviteModal metadata={metadata} onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); refresh(); }} />
      )}
      {showConnect && metadata && (
        <ConnectExistingModal
          metadata={metadata}
          onClose={() => setShowConnect(false)}
          onDone={() => { setShowConnect(false); refresh(); qc.invalidateQueries({ queryKey: ['team-available'] }); }} />
      )}
      {editingPermissions && (
        <ClerkAccessDrawer member={editingPermissions}
          onClose={() => setEditingPermissions(null)} onDone={() => { setEditingPermissions(null); refresh(); }} />
      )}
      {viewingActivity && (
        <ActivityModal member={viewingActivity} onClose={() => setViewingActivity(null)} />
      )}
    </div>
  );
}
